/// <reference path="../types/connection-manager.d.ts" />
import { GlobalApisService } from "@martech-unit-projects/connection-manager";
import { COMMON_SSO_ACCESS_TOKEN } from "../constants/sso";
import { CONNECTION_MANAGER_ENV } from "../constants/config";
import type { IResponse } from "./types";

type InitMethod = (
  source: string,
  env: string
) => {
  subscribe: (handlers: { next?: () => void; error?: (err: IResponse<unknown>) => void }) => void;
};

/**
 * Singleton wrapper around the @martech-unit-projects/connection-manager npm package
 * Provides a centralized instance for all API calls
 */
export class ConnectionManagerClient {
  private static instance: ConnectionManagerClient;
  private globalService: GlobalApisService;

  private constructor() {
    this.globalService = new GlobalApisService();
  }

  /**
   * Get singleton instance of ConnectionManagerClient
   */
  static getInstance(): ConnectionManagerClient {
    if (!ConnectionManagerClient.instance) {
      ConnectionManagerClient.instance = new ConnectionManagerClient();
    }
    return ConnectionManagerClient.instance;
  }

  /**
   * Initialize the connection manager library
   * @param source - Source identifier (e.g., 'WEB')
   * @param env - Environment (e.g., 'PRODUCTION', 'UAT', 'iUAT')
   * @returns Promise that resolves when initialization is complete
   */
  private async initialize(initMethod: InitMethod, source: string, env: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      initMethod.call(this.globalService, source, env).subscribe({
        next: () => resolve(true),
        error: (err: IResponse<unknown>) => reject(err),
      });
    });
  }

  async initLib(source: string, env: string): Promise<boolean> {
    return this.initialize(this.globalService.initLib, source, env);
  }

  async initLibAPP(source: string, env: string): Promise<boolean> {
    return this.initialize(this.globalService.initLibAPP, source, env);
  }

  /**
   * Call API through connection manager gateway
   * @param operationId - Operation identifier
   * @param requestData - Request payload
   * @param connectionFrom - Connection source
   * @param additionalHeaders - Additional headers
   * @param source - Source identifier
   * @returns Promise with API response
   */
  async callApi<TResponse, TRequest = unknown>(
    operationId: string,
    requestData: TRequest,
    connectionFrom: string,
    additionalHeaders: Record<string, string | number>,
    source: string
  ): Promise<IResponse<TResponse>> {
    return this.callApiInternal<TResponse, TRequest>(
      operationId,
      requestData,
      connectionFrom,
      additionalHeaders,
      source,
      false
    );
  }

  async callApiRest<TResponse, TRequest = unknown>(
    operationId: string,
    requestData: TRequest,
    connectionFrom: string,
    additionalHeaders: Record<string, string | number>,
    source: string
  ): Promise<IResponse<TResponse>> {
    return this.callApiInternal<TResponse, TRequest>(
      operationId,
      requestData,
      connectionFrom,
      additionalHeaders,
      source,
      true
    );
  }

  private async callApiInternal<TResponse, TRequest = unknown>(
    operationId: string,
    requestData: TRequest,
    connectionFrom: string,
    additionalHeaders: Record<string, string | number>,
    source: string,
    useRest: boolean
  ): Promise<IResponse<TResponse>> {
    return new Promise((resolve, reject) => {
      const ssoAccessToken =
        typeof localStorage !== "undefined" ? localStorage.getItem(COMMON_SSO_ACCESS_TOKEN) : null;

      const apiCall = useRest
        ? this.globalService.gateWayApiCallingRESTV2<TResponse, TRequest>(
            operationId,
            requestData,
            connectionFrom,
            additionalHeaders,
            source,
            ssoAccessToken,
            CONNECTION_MANAGER_ENV
          )
        : this.globalService.gateWayApiCallingV3<TResponse, TRequest>(
            operationId,
            requestData,
            connectionFrom,
            additionalHeaders,
            source
          );

      apiCall.subscribe({
        next: (res: IResponse<unknown>) => {
          resolve(res as IResponse<TResponse>);
        },
        error: (err: unknown) => {
          // Handle promise-based errors
          if (err instanceof Promise) {
            err.then((actualError) => reject(actualError)).catch(reject);
          } else {
            reject(err);
          }
        },
      });
    });
  }

  /**
   * Reset the client instance (useful for testing)
   */
  static reset(): void {
    ConnectionManagerClient.instance = null!;
  }
}
